import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useEditMetric } from 'src/data/impact/mutation/impact-mutation';
import { useSnackbar } from '../snackbar';
import { useGetUser } from 'src/hooks/useGetUser';
import ReactQuill from 'react-quill';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import 'react-quill/dist/quill.snow.css';
import { Tooltip } from '@mui/material';
import { removeAllTags } from 'src/utils/removeTags';

export const AreaEditMetrics = ({ data, keys }: any) => {
  const user = useGetUser();
  const [textareaValue, setTextareaValue] = useState(data[keys]);
  const [isEditing, setIsEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const editMetric = useEditMetric();
  const editorRef = useRef<HTMLDivElement>(null);
  const isDisabled = (user && user.role !== 'admin') || false;

  const handleEditMetric = useCallback(() => {
    const metricPayload: { [key: string]: string } = {
      [keys]: textareaValue.toString(),
    };
    
    editMetric.mutate(
      {
        ...metricPayload,
        id: data._id || data.id,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('mis à jour avec succès.');
          setIsEditing(false);
        },
        onError: () => {
          enqueueSnackbar("quelque chose s'est mal passé", {
            variant: 'error',
          });
        },
      }
    );
  }, [keys, textareaValue, data._id, data.id, editMetric, enqueueSnackbar]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      editorRef.current && 
      !editorRef.current.contains(event.target as Node)
    ) {
      const toolbar = document.querySelector('.ql-toolbar');
      
      if (toolbar && toolbar.contains(event.target as Node)) {
        return; // Do nothing if clicking inside the toolbar
      }
  
      if (textareaValue !== data[keys]) {
        handleEditMetric();
      } else {
        setIsEditing(false);
      }
    }
  }, [textareaValue, data, keys, handleEditMetric]);
  

  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, handleClickOutside]);

  const handleOnBlur = useCallback(() => {
    // Only save if the value has changed
    if (textareaValue !== data[keys]) {
      handleEditMetric();
    } else {
      setIsEditing(false);
    }
  }, [textareaValue, data, keys, handleEditMetric]);

  const handleQuillChange = (content: string) => {
    setTextareaValue(content);
  };

  const modules = {
    toolbar: [
      ['bold', 'italic'],
      [{ 'color': [
        '#000000', '#e60000', '#ff9900', '#ffff00', 
        '#008a00', '#0066cc', '#9933ff', '#ffffff'
      ] }]
    ]
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 
    'strike', 'color', 'background', 'list', 
    'bullet', 'align'
  ];

  return (
    <div ref={editorRef} className='w-full h-full'>
      {isEditing ? (
        <div className="mt-14 relative w-full ">
          <div className="rich-editor-wrapper  w-full">
            <ReactQuill
              theme="snow"
              value={textareaValue}
              onChange={handleQuillChange}
              modules={modules}
              formats={formats}
              // onBlur={handleOnBlur}
              readOnly={isDisabled}
              className="h-[200px]"
              // onKeyUp={(event) => {
              //   if (event.key === 'Enter') {
              //     handleEditMetric();
              //   }
              // }}
            />
            <button
              onClick={handleEditMetric}
              className="absolute right-2 top-2 p-1 rounded-full hover:bg-gray-700/50 transition-colors"
              title="Save changes"
            >
              <CheckCircleIcon className="text-green-500 hover:text-green-400" />
            </button>
          </div>
        </div>
      ) : (
        <Tooltip title={removeAllTags(textareaValue)}>
        <div
          className="w-full pt-8 h-full bg-transparent font-thin text-[15px] tracking-wide leading-none p-2 resize-none"
          onClick={() => !isDisabled && setIsEditing(true)}
          dangerouslySetInnerHTML={{ __html: textareaValue }}
        />
        </Tooltip>
      )}
    </div>
  );
};
